import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { getMinisite, resolveImg } from '../lib/api';

export default function Minisite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    getMinisite(slug)
      .then(setItem)
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#c9a063]" data-testid="minisite-loading">
        <Loader2 size={32} className="animate-spin" />
      </div>
    );
  }
  if (!item) return null;

  return (
    <div data-testid="minisite-page">
      {/* Header image full-width */}
      {item.header_image && (
        <div className="relative w-full bg-[#0a0a0b] overflow-hidden" style={{ aspectRatio: '3 / 1', maxHeight: '640px' }} data-testid="minisite-header-image">
          <img src={resolveImg(item.header_image)} alt={item.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
        {/* Title block */}
        <header className="mb-10 text-center">
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight" data-testid="minisite-title">
            {item.title}
          </h1>
          {item.subtitle && (
            <p className="mt-5 text-white/70 text-lg md:text-xl italic font-light" data-testid="minisite-subtitle">
              {item.subtitle}
            </p>
          )}
          <div className="mt-6 mx-auto w-16 h-px bg-[#c9a063]" />
        </header>

        {/* Rich content */}
        {item.content && (
          <div
            className="news-content text-white/80 text-base md:text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: item.content }}
            data-testid="minisite-content"
          />
        )}

        {/* CTAs */}
        {item.ctas && item.ctas.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-3 justify-center" data-testid="minisite-ctas">
            {item.ctas.map((c, idx) => {
              const isPrimary = c.style !== 'secondary';
              const cls = isPrimary
                ? 'gd-btn-gold inline-flex items-center gap-2 px-7 py-3.5 text-[11px] uppercase tracking-[0.3em] font-medium'
                : 'inline-flex items-center gap-2 px-7 py-3.5 text-[11px] uppercase tracking-[0.3em] font-medium border border-[#c9a063]/60 text-[#c9a063] hover:bg-[#c9a063]/10 transition-colors';
              return (
                <a
                  key={idx}
                  href={c.url}
                  target={c.url?.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  className={cls}
                  data-testid={`minisite-cta-${idx}`}
                >
                  {c.label} <ExternalLink size={12} />
                </a>
              );
            })}
          </div>
        )}

        {/* Gallery */}
        {item.gallery && item.gallery.length > 0 && (
          <section className="mt-16" data-testid="minisite-gallery">
            <div className="text-center mb-6">
              <div className="text-[10px] tracking-[0.4em] text-[#c9a063] uppercase">Galería</div>
              <div className="mt-3 mx-auto w-12 h-px bg-[#c9a063]/50" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {item.gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(src)}
                  className="aspect-square overflow-hidden bg-[#1a1a1d] group"
                  data-testid={`minisite-gallery-item-${i}`}
                >
                  <img
                    src={resolveImg(src)}
                    alt={`Imagen ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="minisite-lightbox"
        >
          <img src={resolveImg(lightbox)} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
